import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Input, InputRef, Modal, Tag } from 'antd';
import { goodReceive } from '../../model/good-receive';
import { initProduct, product } from '../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CommonDataTable from '../common/common-data-table';
import { api_good_receive, api_product, api_product_lookup_code, api_purchase_order } from '../../api/product';
import CommonSearch from '../common/common-search';
import { ExclamationCircleFilled, CloseOutlined } from '@ant-design/icons';
import { isEmpty } from '../../utils/util';
import { backendMessage } from '../../utils/write-result';
import CommonDetail from '../common/common-detail';
import GoodReceiveProduct from './good-receive-product';
import ProductDetail from '../product/product-detail';

interface props {
    goodReceive: goodReceive,
    onChange: React.Dispatch<React.SetStateAction<object>>,
    onSaveDraft?: () => void,
    onConfirmReceive?: () => void
}

const MSG_CLEAR_CONFIRM = 'ต้องการล้างจำนวนรับของสินค้าทุกรายการเป็น 0 หรือไม่?';
const MSG_RESTORE_CONFIRM = 'ต้องการคืนค่าจำนวนรับตามจำนวนคงเหลือใน PO หรือไม่?';
const MSG_PRODUCT_NOT_IN_PO = 'สินค้านี้ไม่ได้อยู่ในใบสั่งซื้อ PO นี้';
const MSG_OVER_RECEIVE = 'จำนวนรับเกินจำนวนคงเหลือใน PO';

/**
 * Current remaining quantity of a PO line (ordered - already CONFIRMED received).
 * The backend excludes Draft Goods Receives from `remaining_quantity`, so this is
 * the authoritative cap for what this draft may still receive.
 */
const getPoRemaining = (line: any) => {
    if (!line) return 0;
    const remaining = line.remaining_quantity;
    if (remaining !== undefined && remaining !== null && !isNaN(Number(remaining))) {
        return Number(remaining);
    }
    return Number(line.quantity) || 0;
};

/**
 * Matches a PO line against either a Goods Receive detail row (product id in `product`)
 * or a product looked up by barcode/search (product id in `id`).
 */
const findPoLine = (lines: any[], target: any) => {
    if (!Array.isArray(lines) || lines.length === 0 || !target) return undefined;
    const productId = target.product !== undefined && target.product !== null ? target.product : target.id;
    const productCode = target.product_code;

    return lines.find((line: any) => {
        if (productId !== undefined && productId !== null && line.product !== undefined && line.product !== null) {
            if (Number(line.product) === Number(productId)) return true;
        }
        if (productCode && line.product_code) {
            return String(line.product_code) === String(productCode);
        }
        return false;
    });
};

/** The VAT choices, and the one place that turns one into words. */
export const VAT_OPTIONS = [
    { id: -1, name: 'ไม่ระบุ' },
    { id: 0, name: 'Vat out' },
    { id: 1, name: 'Vat in' }
];

export const vatLabel = (value: any): string => {
    // Absence is checked FIRST, and this order is load-bearing: `Number(null)`
    // and `Number('')` are both 0, which is the code for "Vat out". Looking up
    // the option first therefore labelled a line with no recorded VAT as
    // exclusive-VAT -- asserting a tax treatment nobody entered, which is
    // worse than the raw -1 it replaced.
    if (value === null || value === undefined || String(value).trim() === '') return 'ไม่ระบุ';
    const found = VAT_OPTIONS.find((o) => o.id === Number(value));
    // An unrecognised code is shown as itself rather than guessed at.
    return found ? found.name : String(value);
};

const GoodReceiveDetail: React.FC<props> = (prop) => {

    const productCodeRef = useRef<InputRef>(null);

    const [open, setOpen] = useState(false);
    const [openAddProduct, setOpenAddProduct] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [tableSelect, setTableSelect] = useState('');

    const [data, setData] = useState<any>({});
    const [product, setProduct] = useState<any>({});
    const [productData, setProductData] = useState<any[]>([]);
    const [productList, setProductList] = useState<any[]>([]);
    const [addProduct, setAddProduct] = useState<product>(initProduct);

    const [productCode, setProductCode] = useState('');
    const [productQty, setProductQty] = useState(1);
    const [vendorName, setVendorName] = useState('');
    const [purchaseOrderDocNo, setPurchaseOrderDocNo] = useState('');
    const [remark, setRemark] = useState('');

    // Detail lines of the linked Purchase Order, carrying current remaining quantities.
    const [poLines, setPoLines] = useState<any[]>([]);

    const [totalQuantity, setTotalQuantity] = useState(0);
    const [totalWeight, setTotalWeight] = useState(0);
    const [totalCost, setTotalCost] = useState(0);

    const [isContinue, setIsContinue] = useState(false);

    // status 2 = Confirmed. A confirmed Goods Receive is read-only.
    const isDraftEditable = data.status !== 2;
    const isPoLinked = Number(data.purchase_order) > 0;

    const productColumn: any = [
        { title: 'ลำดับ', dataIndex: 'rec', key: 'rec', width: '70px', fixed: 'left', edit: false },
        { title: 'รหัสสินค้า', dataIndex: 'product_code', key: 'product_code', type: 'LINK', width: '160px', fixed: 'left', edit: false },
        { title: 'ชื่อสินค้า', dataIndex: 'product_name', key: 'product_name', edit: false },
        { title: 'จำนวน', dataIndex: 'quantity', key: 'quantity', type: isDraftEditable ? 'INPUT' : undefined, width: '100px', edit: isDraftEditable },
        { title: 'น้ำหนัก', dataIndex: 'weight', key: 'weight', type: isDraftEditable ? 'INPUT' : undefined, width: '100px', edit: isDraftEditable },
        { title: 'ต้นทุนรวม', dataIndex: 'total_cost', key: 'total_cost', type: isDraftEditable ? 'INPUT' : undefined, width: '160px', edit: isDraftEditable },
        { title: 'ราคาต้นทุนเดิม', dataIndex: 'product_cost', key: 'product_cost', width: '160px', edit: false },
        { title: 'ราคาต้นทุน', dataIndex: 'cost', key: 'cost', type: isDraftEditable ? 'INPUT' : undefined, width: '160px', edit: isDraftEditable },
        { title: 'ราคาขายเดิม', dataIndex: 'price_retail', key: 'price_retail', width: '160px', edit: false },
        { title: 'ราคาขาย', dataIndex: 'price', key: 'price', type: isDraftEditable ? 'INPUT' : undefined, width: '160px', edit: isDraftEditable },
        {
            // `-1` is the sentinel for "not specified" -- Backend returns it
            // for a NULL column too (`COALESCE(gd.vat, -1)`). In edit mode
            // the SELECT showed the label; with no `type` the view and the
            // preview printed the raw number, so a received slip read
            // "Vat -1". Same values, rendered in words.
            title: 'Vat', dataIndex: 'vat', key: 'vat',
            type: isDraftEditable ? 'SELECT' : undefined,
            width: '100px', edit: isDraftEditable, listDefault: -1,
            list: VAT_OPTIONS,
            render: isDraftEditable ? undefined : (value: any) => vatLabel(value)
        }
    ]

    const recalculateTotals = (targetData?: any, notifyParent: boolean = true) => {
        const currentData = targetData ? { ...targetData } : { ...data };
        const details = (Array.isArray(currentData.detail) ? currentData.detail : []).map((d: any, idx: number) => ({
            ...d,
            key: d.key || (idx + 1),
            rec: d.rec || (idx + 1),
            status: d.status || 'Y'
        }));

        let totalQty = 0;
        let totalWt = 0;
        let totalCt = 0;

        details.forEach((item: any) => {
            const q = Number(item.quantity) || 0;
            const w = Number(item.weight) || 0;
            const c = Number(item.cost) || 0;
            const rowTotalCost = item.total_cost !== undefined && item.total_cost !== '' && !isNaN(Number(item.total_cost))
                ? Number(item.total_cost)
                : Number((q * c).toFixed(2));

            totalQty += q;
            totalWt += w;
            totalCt += rowTotalCost;
        });

        totalCt = parseFloat(totalCt.toFixed(2));
        totalWt = parseFloat(totalWt.toFixed(2));

        currentData.quantity = totalQty;
        currentData.weight = totalWt;
        currentData.cost = totalCt;
        currentData.detail = details;

        setData(currentData);
        setVendorName(currentData.vendor_name != null ? currentData.vendor_name : '');
        setPurchaseOrderDocNo(currentData.purchase_order_doc_no != null ? currentData.purchase_order_doc_no : '');
        setRemark(currentData.remark != null ? currentData.remark : '');
        setProductData([...details]);
        setTotalQuantity(totalQty);
        setTotalWeight(totalWt);
        setTotalCost(totalCt);

        if (notifyParent) {
            prop.onChange(currentData);
        }
        return currentData;
    };

    /**
     * Fetches the linked PO so the draft knows each product's CURRENT remaining
     * quantity. Used to cap barcode scanning and to restore quantities.
     */
    const loadPoLines = async (poId: any) => {
        const id = Number(poId) || 0;
        if (id <= 0) {
            setPoLines([]);
            return [];
        }
        try {
            const poData = await api_purchase_order('GET', {}, id);
            const lines = poData && Array.isArray(poData.detail) ? poData.detail : [];
            setPoLines(lines);
            return lines;
        } catch (e) {
            console.error('Error fetching purchase order detail:', e);
            setPoLines([]);
            return [];
        }
    };

    const init = async () => {
        let baseData: any = prop.goodReceive ? { ...prop.goodReceive } : { ...data };
        if (prop.goodReceive && prop.goodReceive.id > 0) {
            setIsUpdate(true);
            if (!Array.isArray(baseData.detail) || baseData.detail.length === 0) {
                try {
                    let getGoodReceive = await api_good_receive('GET', {}, prop.goodReceive.id);
                    if (getGoodReceive && getGoodReceive.id) {
                        baseData = getGoodReceive;
                    }
                } catch (e) {
                    console.error('Error fetching good receive detail:', e);
                }
            }
        }

        recalculateTotals(baseData, false);
        await loadPoLines(baseData.purchase_order);
    };

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    const setInputData = (object: any) => {
        setVendorName(object.vendor_name != null ? object.vendor_name : '');
        setPurchaseOrderDocNo(object.purchase_order_doc_no != null ? object.purchase_order_doc_no : '');
        setRemark(object.remark != null ? object.remark : '');
        setProductData(object.detail != null ? object.detail : []);
        setTotalQuantity(object.quantity != null ? object.quantity : 0);
        setTotalWeight(object.weight != null ? object.weight : 0);
        setTotalCost(object.cost != null ? object.cost : 0);
    }

    const onDataChange = (e: any) => {
        let key: string = String(e.target.name);
        let value: any = (
            e.target.type === 'checkbox'
                ? (e.target.checked ? 1 : 0)
                : e.target.type === 'radio' ? (e.target.value ? 1 : 0)
                : typeof(e.target.value) === 'number' ? Number(e.target.value)
                : String(e.target.value)
        );
        let object: any = { ...data };
        object[key] = value;

        setData(object);
        if (key === 'remark') {
            setRemark(value);
        }
        prop.onChange(object);
    }

    const onProductChange = (e: any) => {
        setProductCode(e.target.value);
    }

    const onProductQtyChange = (e: any) => {
        setProductQty(isEmpty(e.target.value) ? 0 : parseInt(e.target.value));
    }

    const handleProductSearchEnter = async (explicitCode?: string) => {
        const rawCode = explicitCode !== undefined ? explicitCode : productCode;
        const code = String(rawCode || '').trim();
        if (isEmpty(code)) return;

        try {
            const targetProduct = await api_product_lookup_code(code);
            if (targetProduct && (targetProduct.id || targetProduct.product_code)) {
                addProductGuarded(targetProduct);
            } else {
                Modal.warning({
                    ...getConfigModal('แจ้งเตือน', 'ไม่พบสินค้า')
                });
            }
        } catch (err) {
            console.error('Barcode lookup error:', err);
            Modal.error({
                title: 'ข้อผิดพลาด',
                icon: <ExclamationCircleFilled rev={undefined} />,
                content: 'ค้นหาสินค้าไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ',
                okText: 'ตกลง',
                centered: true
            });
        } finally {
            setProductCode('');
            setProductQty(1);
            setTimeout(() => {
                productCodeRef.current?.focus();
            }, 50);
        }
    };

    const onSearchInputChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            e.stopPropagation();
            let code = (e.target as HTMLInputElement).value || productCode;
            if ((e.target as HTMLInputElement).name === 'product_qty') {
                code = productCode;
            }
            handleProductSearchEnter(code);
        }
    };

    const onOpenSelect = (table: string) => {
        setTableSelect(table);
        setIsOpen(true);
    }

    /** Builds a Goods Receive detail row from a Purchase Order line. */
    const buildRowFromPoLine = (item: any, index: number, receiveQty: number) => {
        const unitCost = Number(item.cost) || 0;
        const qty = Number(receiveQty) || 0;

        return {
            key: index + 1,
            id: 0,
            good_receive: 0,
            product: item.product,
            product_code: item.product_code,
            product_name: item.product_name,
            product_name_other: item.product_name_other || '',
            ordered_quantity: Number(item.ordered_quantity) || Number(item.quantity),
            remaining_quantity: getPoRemaining(item),
            quantity: qty,
            weight: Number(item.weight) || 0,
            cost: unitCost,
            total_cost: parseFloat((qty * unitCost).toFixed(2)),
            lot: '',
            expire_date: '',
            remark: item.remark || '',
            status: 'Y',
            vat: item.vat !== undefined ? item.vat : -1,
            product_type: item.product_type,
            product_unit: item.product_unit,
            product_cost: item.cost,
            price_retail: item.price_retail,
            price: item.price_retail,
            price_wholesale: item.price_wholesale,
            price_retail_member: item.price_retail_member,
            price_wholesale_member: item.price_wholesale_member
        };
    };

    /**
     * PO-LINKED GR PROTECTION.
     * On a GR linked to a PO the document may only contain PO products, and no line
     * may exceed the PO's current remaining quantity. `silent` is used for bulk adds
     * (vendor import) where a modal per skipped product would be noise.
     * Returns true when the product was added/incremented.
     */
    const addProductGuarded = (targetProduct: any, silent: boolean = false) => {
        const poId = Number(data.purchase_order) || 0;

        if (poId > 0) {
            const poLine = findPoLine(poLines, targetProduct);
            if (!poLine) {
                if (!silent) {
                    Modal.warning({ ...getConfigModal('แจ้งเตือน', MSG_PRODUCT_NOT_IN_PO) });
                }
                return false;
            }

            const details = Array.isArray(data.detail) ? data.detail : [];
            const existingRow = findPoLine(details, targetProduct);
            const currentQty = Number(existingRow && existingRow.quantity) || 0;
            const addQty = isEmpty(productQty) || productQty <= 0 ? 1 : Number(productQty);
            const remaining = getPoRemaining(poLine);

            if (currentQty + addQty > remaining) {
                if (!silent) {
                    Modal.warning({ ...getConfigModal('แจ้งเตือน', MSG_OVER_RECEIVE) });
                }
                return false;
            }
        }

        onManageProduct('A', targetProduct);
        return true;
    }

    /**
     * Picking a vendor or a purchase order from the search dialog.
     *
     * This had no error handling at all, while `api_product` has always
     * thrown on failure and `api_purchase_order` now does too: a lookup
     * that failed produced an unhandled rejection and the dialog simply
     * did nothing, with no clue why. Any failure here is a failed lookup,
     * so it says so and leaves the form as it was.
     */
    const handleSelected = async (e: any) => {
        try {
            await selectFromDialog(e);
        } catch (err: any) {
            console.error('Selection lookup failed:', err);
            Modal.error({
                ...getConfigModal(
                    'โหลดข้อมูลไม่สำเร็จ',
                    backendMessage(err, 'ไม่สามารถโหลดข้อมูลที่เลือกได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง'),
                ),
            });
        }

        if (!isContinue) {
            setIsOpen(false);
        }
    }

    const selectFromDialog = async (e: any) => {
        if (e.table == 'product') {
            addProductGuarded(e);
        } else if (e.table == 'vendor') {
            let object: any = { ...data };
            object.vendor = e.id;
            object.vendor_name = e.name;
            setVendorName(e.name);
            setData(object);
            prop.onChange(object);

            let getProduct = await api_product('GET_BY_VALUE', {}, e.id);
            if (getProduct && getProduct.length > 0) {
                // On a PO-linked GR, silently skip vendor products that are not on the PO.
                getProduct.forEach((p: any) => {
                    addProductGuarded(p, true);
                });
            }
        } else if (e.table == 'purchase_order') {
            let poData = await api_purchase_order('GET', {}, e.id);
            if (poData && poData.id) {
                let object: any = { ...data };
                object.purchase_order = poData.id;
                object.purchase_order_doc_no = poData.document_no;
                object.vendor = poData.vendor;
                object.vendor_name = poData.vendor_name;

                const poDetail = Array.isArray(poData.detail) ? poData.detail : [];
                setPoLines(poDetail);

                if (poDetail.length > 0) {
                    // Pre-fill with the CURRENT remaining quantity; fully received lines are skipped.
                    let newDetails: any[] = [];
                    poDetail.forEach((item: any) => {
                        const remaining = getPoRemaining(item);
                        if (remaining <= 0) return;
                        newDetails.push(buildRowFromPoLine(item, newDetails.length, remaining));
                    });
                    object.detail = newDetails;
                } else {
                    object.detail = [];
                }

                recalculateTotals(object);
            }
        }
    }

    const cancelSelected = (key: string) => {
        if (key == 'vendor') {
            let object: any = { ...data };
            object.vendor = null;
            object.vendor_name = '';
            setVendorName('');
            setData(object);
            prop.onChange(object);
        } else if (key == 'purchase_order') {
            let object: any = { ...data };
            object.purchase_order = null;
            object.purchase_order_doc_no = '';
            setPurchaseOrderDocNo('');
            setPoLines([]);
            recalculateTotals(object);
        }
    }

    const onManageProduct = (key: string, e: any) => {
        let object: any = { ...data };
        object.detail = Array.isArray(object.detail) ? [...object.detail] : [];

        if (key == 'A') {
            let index = object.detail.findIndex((x: any) => {
                let status = false;
                
                if (x['product_code'] == e['product_code']) {
                    status = true;
                }

                if (e['id'] < 0) {
                    if (x['product'] == e['id'] && x['product_code'] == e['product_code']) {
                        status = true;
                    } else {
                        status = false;
                    }
                }
                return status;
            });
            if (index >= 0) {
                let product_data = object.detail[index];
                let qty = product_data['quantity'];

                if (product_data['id'] < 0 && e['id'] > 0) {
                    object.detail[index] = {
                        ...object.detail[index],
                        id: 0,
                        product: e['id'],
                        vat: e['vat'],
                        product_code: e['product_code'],
                        product_name: e['product_name'],
                        product_name_other: e['product_name_other'],
                        product_cost: e['product_cost'],
                        product_type: e['product_type'],
                        product_unit: e['product_unit'],
                        price_retail: e['price_retail'],
                        price: e['price_retail'],
                        price_wholesale: e['price_wholesale'],
                        price_retail_member: e['price_retail_member'],
                        price_wholesale_member: e['price_wholesale_member']
                    };
                } else {
                    const currentQty = parseInt(isEmpty(qty) ? 0 : qty);
                    const addQty = isEmpty(productQty) || productQty <= 0 ? 1 : productQty;
                    const newQty = currentQty + addQty;
                    const existingTotalCost = Number(object.detail[index]['total_cost']) || 0;
                    const unitCost = Number(object.detail[index]['cost'] || object.detail[index]['product_cost'] || 0);

                    object.detail[index]['quantity'] = newQty;

                    if (existingTotalCost > 0) {
                        // Preserved total_cost remains intact across scans
                        object.detail[index]['total_cost'] = existingTotalCost;
                        object.detail[index]['cost'] = unitCost > 0 ? unitCost : parseFloat((existingTotalCost / newQty).toFixed(2));
                    } else {
                        object.detail[index]['cost'] = unitCost;
                        object.detail[index]['total_cost'] = parseFloat((newQty * unitCost).toFixed(2));
                    }
                }
            } else {
                const addQty = isEmpty(productQty) || productQty <= 0 ? 1 : productQty;
                const unitCost = Number(e['product_cost'] || 0);
                object.detail = [
                    ...object.detail,
                    {
                        key: (object.detail.length + 1),
                        id: e['id'] < 0 ? e['id'] : 0,
                        good_receive: 0,
                        quantity: addQty,
                        weight: 0,
                        cost: unitCost,
                        total_cost: parseFloat((addQty * unitCost).toFixed(2)),
                        lot: '',
                        expire_date: '',
                        remark: '',
                        status: "Y",
                        product: e['id'],
                        vat: e['vat'],
                        product_code: e['product_code'],
                        product_name: e['product_name'],
                        product_name_other: e['product_name_other'],
                        product_cost: e['product_cost'],
                        product_type: e['product_type'],
                        product_unit: e['product_unit'],
                        price_retail: e['price_retail'],
                        price: e['price_retail'],
                        price_wholesale: e['price_wholesale'],
                        price_retail_member: e['price_retail_member'],
                        price_wholesale_member: e['price_wholesale_member'],
                    }
                ];
            }
        } else if (key == 'E') {
            const index = object.detail.findIndex((x: any) => {
                return x['key'] == e['key']
            });
            if (index >= 0) {
                object.detail[index] = { ...e };
            }
        }

        setProductQty(1);
        recalculateTotals(object);
    }

    const onActionProduct = (e: any) => {
        let object: any = { ...data };
        object.detail = Array.isArray(object.detail) ? [...object.detail] : [];
        let count = 0;

        if (Array.isArray(e) && e.length > 0 && 'mode' in e[0]) {
            let mode = e[0].mode;
            let key = e[0].key;

            if (mode == 'remove') {
                let detail: any = [];

                object.detail.forEach((x: any) => {
                    if (x.key != key) {
                        count++;
                        detail.push({ ...x, key: count });
                    }
                });
                object.detail = detail;
                recalculateTotals(object);
            } else if (mode == 'edit') {
                setProduct(object.detail.filter((x: any) => { return x['key'] == key})[0]);
                setOpen(true);
            } else if (mode == 'link') {
                let item = JSON.parse(JSON.stringify(e[0].data))
                let item_product = initProduct;
                item_product['id'] = 99999999;
                item_product['product_code'] = item['product_code'];
                item_product['product_name'] = item['product_name'];
                item_product['quantity'] = 0;
                item_product['product_cost'] = item['cost'];
                setAddProduct(item_product);
                setOpenAddProduct(true);
            }
        } else if (Array.isArray(e)) {
            const previousDetails = object.detail || [];
            const updatedDetails = e.map((x: any, idx: number) => {
                const prev = previousDetails.find((p: any) => p.key === x.key) || previousDetails[idx] || {};
                const q = Number(x['quantity']) || 0;
                const prevQ = Number(prev['quantity']) || 0;
                const prevCost = Number(prev['cost']) || 0;
                const prevTotalCost = Number(prev['total_cost']) || 0;
                const currentCost = Number(x['cost']) || 0;
                const currentTotalCost = Number(x['total_cost']) || 0;

                let newCost = currentCost;
                let newTotalCost = currentTotalCost;

                // Did total_cost change? (User edited total_cost directly)
                if (currentTotalCost !== prevTotalCost && currentCost === prevCost) {
                    newTotalCost = currentTotalCost;
                    if (q > 0) {
                        newCost = parseFloat((currentTotalCost / q).toFixed(2));
                    } else {
                        newCost = prevCost > 0 ? prevCost : currentCost;
                    }
                }
                // Did cost change? (User edited unit cost directly)
                else if (currentCost !== prevCost && currentTotalCost === prevTotalCost) {
                    newCost = currentCost;
                    if (q > 0) {
                        newTotalCost = parseFloat((q * currentCost).toFixed(2));
                    } else {
                        newTotalCost = prevTotalCost > 0 ? prevTotalCost : currentTotalCost;
                    }
                }
                // Did quantity change? (User edited quantity)
                else if (q !== prevQ) {
                    if (q > 0) {
                        if (newCost > 0) {
                            newTotalCost = parseFloat((q * newCost).toFixed(2));
                        } else if (newTotalCost > 0) {
                            newCost = parseFloat((newTotalCost / q).toFixed(2));
                        }
                    } else {
                        // q === 0: preserve both cost and total_cost
                        newCost = prevCost > 0 ? prevCost : newCost;
                        newTotalCost = prevTotalCost > 0 ? prevTotalCost : newTotalCost;
                    }
                }

                return {
                    ...x,
                    cost: newCost,
                    total_cost: newTotalCost
                };
            });
            object.detail = updatedDetails;
            recalculateTotals(object);
        }
    }

    const onEditedProduct = () => {
        if (isEmpty(product['cost']) || isEmpty(product['quantity']) || product['quantity'] < 0 || isEmpty(product['weight']) || product['weight'] < 0 ||
            isEmpty(product['price_retail']) || isEmpty(product['price_wholesale']) || isEmpty(product['price_retail_member']) || isEmpty(product['price_wholesale_member'])) {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
            });
        } else {
            onManageProduct('E', product);
            setOpen(false);
        }
    }

    const setFooter = () => {
        return <>
            <br />
            <Checkbox name='is_continue' style={{marginRight: '20px'}} checked={isContinue} onChange={(e: any) => {setIsContinue(e.target.checked)}}>ทำรายการต่อเนื่อง</Checkbox>
            <Button className='btn-cancel' onClick={() => { setIsOpen(false); setTableSelect(''); setIsContinue(false); }}>ยกเลิก</Button>
        </>
    }

    const calculateTotal = () => {
        recalculateTotals();
    }

    const focusBarcodeInput = () => {
        setProductCode('');
        setProductQty(1);
        setTimeout(() => {
            productCodeRef.current?.focus();
        }, 50);
    }

    /**
     * "ล้างจำนวนรับ" — resets the ACTUAL RECEIVED quantity and weight of every line to 0
     * so the user can count the delivery by physically scanning each item.
     * Product rows and all metadata (product, code, name, PO reference, vendor,
     * unit cost, line total_cost, prices, vat) are preserved.
     */
    const onClearReceivedQuantities = () => {
        if (!isDraftEditable) return;

        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', MSG_CLEAR_CONFIRM),
            ...{
                onOk() {
                    const currentDetails = Array.isArray(productData) && productData.length > 0
                        ? productData
                        : (Array.isArray(data?.detail) ? data.detail : []);

                    const details = currentDetails.map((row: any) => {
                        const unitCost = Number(row.cost) || Number(row.product_cost) || 0;
                        const existingTotalCost = Number(row.total_cost);
                        const preservedTotalCost = !isNaN(existingTotalCost) && existingTotalCost > 0
                            ? existingTotalCost
                            : parseFloat(((Number(row.ordered_quantity) || Number(row.quantity) || 1) * unitCost).toFixed(2));
                        return {
                            ...row,
                            quantity: 0,
                            weight: 0,
                            cost: unitCost,
                            total_cost: preservedTotalCost
                        };
                    });

                    let object: any = { ...data, detail: details };
                    recalculateTotals(object);
                    focusBarcodeInput();
                }
            }
        });
    }

    /**
     * "คืนค่าตาม PO" — restores each line to the PO's CURRENT remaining quantity
     * (ordered minus already CONFIRMED received), never the original ordered quantity.
     * Re-reads the PO so the cap reflects other confirmed receipts.
     */
    const onRestorePoQuantities = () => {
        if (!isDraftEditable || !isPoLinked) return;

        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', MSG_RESTORE_CONFIRM),
            ...{
                async onOk() {
                    const lines = await loadPoLines(data.purchase_order);
                    if (!Array.isArray(lines) || lines.length === 0) {
                        Modal.warning({
                            ...getConfigModal('แจ้งเตือน', 'ไม่พบรายการสินค้าในใบสั่งซื้อ PO นี้')
                        });
                        return;
                    }

                    let object: any = { ...data };
                    const currentDetails = Array.isArray(object.detail) ? object.detail : [];
                    const restoredLineIds = new Set<string>();

                    const restored = currentDetails.map((row: any) => {
                        const poLine = findPoLine(lines, row);
                        if (!poLine) {
                            // Not part of the PO: nothing to restore, so it receives nothing.
                            return { ...row, quantity: 0, weight: 0, total_cost: 0 };
                        }
                        restoredLineIds.add(String(poLine.product));

                        const remaining = getPoRemaining(poLine);
                        const unitCost = Number(row.cost) || Number(poLine.cost) || 0;
                        return {
                            ...row,
                            ordered_quantity: Number(poLine.ordered_quantity) || Number(poLine.quantity),
                            remaining_quantity: remaining,
                            quantity: remaining,
                            weight: Number(poLine.weight) || 0,
                            total_cost: parseFloat((remaining * unitCost).toFixed(2))
                        };
                    });

                    // Re-add PO lines that are still outstanding but missing from the draft.
                    lines.forEach((line: any) => {
                        if (restoredLineIds.has(String(line.product))) return;
                        const remaining = getPoRemaining(line);
                        if (remaining <= 0) return;
                        restored.push(buildRowFromPoLine(line, restored.length, remaining));
                    });

                    object.detail = restored;
                    recalculateTotals(object);
                    focusBarcodeInput();
                }
            }
        });
    }

    const onAddProductClosed = () => {
        setProductCode('');
        setProductQty(1);
        productCodeRef.current?.focus();
        setOpenAddProduct(false);
    }

    const onSaveProductSuccess = async (code: string) => {
        setAddProduct(initProduct); 
        setOpenAddProduct(false);
        let getProductList = await api_product('LIST', {});
        setProductList(getProductList);
        let getProduct = getProductList.filter((x: any) => { return x['product_code'] == code });
        if (getProduct.length > 0) {
            onManageProduct('A', getProduct[0]);
            setProductCode('');
            setProductQty(1);
            productCodeRef.current?.focus();
        }
    }

    const onSaveProduct = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(addProduct));

        // if (!isEmpty(body['product_code']) && body['product_type'] > 0 && body['product_unit'] > 0 && !isEmpty(body['product_cost']) && !isEmpty(body['price_retail'])) {
        //     body['product_cost'] = isEmpty(body['product_cost']) ? null : body['product_cost']
        //     body['price_retail'] = isEmpty(body['price_retail']) ? null : body['price_retail']
        //     body['price_wholesale'] = isEmpty(body['price_wholesale']) ? null : body['price_wholesale']
        //     body['price_retail_member'] = isEmpty(body['price_retail_member']) ? null : body['price_retail_member']
        //     body['price_wholesale_member'] = isEmpty(body['price_wholesale_member']) ? null : body['price_wholesale_member']
        //     body['init_order_amount'] = isEmpty(body['init_order_amount']) ? null : body['init_order_amount']
        //     body['point_purchase'] = isEmpty(body['point_purchase']) ? null : body['point_purchase']
        //     body['limit_quantity'] = isEmpty(body['limit_quantity']) ? null : body['limit_quantity']
        //     body['point'] = isEmpty(body['point']) ? null : body['point']
        //     body['vendor'] = body['vendor'].length <= 0 ? "" : JSON.stringify(body['vendor']).replace('[', '').replace(']', '') + ","
        //     body['remark'] = isEmpty(body['remark']) ? null : body['remark']
        //     body['vat'] = body['vat'] < 0 ? null : body['vat']
        //     body['tier_price'] = isEmpty(body['tier_price']) ? "[]" : body['tier_price']

        //     let res = await api_product(operation, body, key_id);
        //     if (res === 1) {
        //         setOpen(false);
        //         Modal.success({
        //             ...getConfigModal('สำเร็จ', 'เพิ่มข้อมูลสำเร็จแล้ว'),
        //             ...{ onOk() {onSaveProductSuccess(body['product_code']);}}
        //         });
        //     } else {
        //         Modal.error({
        //             ...getConfigModal('ผิดพลาด', 'เพิ่มข้อมูลไม่สำเร็จ'),
        //         });
        //     }
        // } else {
        //     Modal.warning({
        //         ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
        //     });
        // }

    }

    useEffect(() => {
        init();
    }, [prop.goodReceive]);
    
    return (
        <>
            <div className='detailContainer'>
                <div className='detailBox' style={{'marginBottom': '50px', 'maxHeight': '50%'}}>
                    {isDraftEditable ? (
                        <div className='searchDataBox' style={{ margin: '0 0 12px 0', gap: '8px', flexWrap: 'wrap' }}>
                            <Button
                                className='btn-common'
                                onClick={onClearReceivedQuantities}
                                disabled={productData.length === 0}
                                title='ตั้งจำนวนรับของสินค้าทุกรายการเป็น 0 เพื่อเริ่มนับด้วยการสแกนบาร์โค้ด'
                                data-testid="btn-clear-received-qty"
                            >
                                ล้างจำนวนรับ
                            </Button>
                            {isPoLinked ? (
                                <Button
                                    className='btn-common'
                                    onClick={onRestorePoQuantities}
                                    title='คืนค่าจำนวนรับตามจำนวนคงเหลือปัจจุบันในใบสั่งซื้อ'
                                    data-testid="btn-restore-po-qty"
                                >
                                    คืนค่าตาม PO
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                        {isDraftEditable ? (
                            <div className='searchDataBox' style={{ margin: 0 }}>
                                <label>ค้นหาสินค้า</label>
                                <Input 
                                    ref={productCodeRef} 
                                    name="product_code" 
                                    value={productCode} 
                                    placeholder="สแกนบาร์โค้ด หรือพิมพ์รหัสสินค้า แล้วกด Enter"
                                    onChange={onProductChange} 
                                    onKeyDown={onSearchInputChange}
                                    data-testid="input-good-receive-product-code"
                                    style={{ minWidth: '220px' }}
                                />
                                <label>จำนวน</label>
                                <Input 
                                    name="product_qty" 
                                    value={productQty} 
                                    placeholder="" 
                                    style={{ width: '60px' }} 
                                    onChange={onProductQtyChange} 
                                    onKeyDown={onSearchInputChange}
                                    data-testid="input-good-receive-product-qty"
                                />
                                <Button className='btn-common' onClick={() => {onOpenSelect('product')}} data-testid="btn-open-search-product">
                                    <FontAwesomeIcon icon="magnifying-glass" />
                                </Button>
                            </div>
                        ) : (
                            <div style={{ fontWeight: 600, color: '#64748B', fontSize: '14px' }}>
                                📋 รายการสินค้าที่รับเข้าแล้ว ({productData.length} รายการ)
                            </div>
                        )}
                        <div>
                            {data.status === 2 ? (
                                <Tag color="green" style={{ fontSize: '13px', padding: '4px 10px', fontWeight: 700 }}>
                                    🟢 รับเข้าแล้ว (Confirmed)
                                </Tag>
                            ) : (
                                <Tag color="orange" style={{ fontSize: '13px', padding: '4px 10px', fontWeight: 700 }}>
                                    🟡 ฉบับร่าง (Draft)
                                </Tag>
                            )}
                        </div>
                    </div>
                    <CommonDataTable columns={productColumn} data={[...productData]} operation={isDraftEditable ? 'OUT_TABLE' : 'NONE'} scollX={1640} onChange={onActionProduct} />
                </div>
                <div className='footerBox'>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>ผู้ขายส่งสินค้า</label>
                                <div>
                                    <Input name="vendor_name" value={vendorName} placeholder="ผู้ขายส่งสินค้า" 
                                        suffix={
                                            isDraftEditable ? (
                                                <CloseOutlined rev={undefined} style={{color: '#000099', cursor: 'pointer', display: (vendorName != '' ? 'block' : 'none')}} onClick={() => { cancelSelected('vendor') }} />
                                            ) : null
                                        } 
                                        disabled={!isDraftEditable || vendorName == ''} 
                                    />
                                    {isDraftEditable && (
                                        <Button className='btn-common' onClick={() => {onOpenSelect('vendor')}}><FontAwesomeIcon icon="magnifying-glass" /></Button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>เลขที่ใบสั่งซื้อ</label>
                                <div>
                                    <Input 
                                        name="purchase_order_doc_no" 
                                        value={purchaseOrderDocNo} 
                                        placeholder="เลขที่ใบสั่งซื้อ" 
                                        disabled 
                                        suffix={
                                            isDraftEditable ? (
                                                <CloseOutlined rev={undefined} style={{color: '#000099', cursor: 'pointer', display: (purchaseOrderDocNo != '' ? 'block' : 'none')}} onClick={() => { cancelSelected('purchase_order') }} />
                                            ) : null
                                        }
                                    />
                                    {isDraftEditable && (
                                        <Button className='btn-common' onClick={() => {onOpenSelect('purchase_order')}}><FontAwesomeIcon icon="magnifying-glass" /></Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>จำนวนรวม</label>
                                <div>
                                    <Input name="total_quantity" value={totalQuantity} placeholder="จำนวนรวม" disabled />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>น้ำหนักรวม</label>
                                <div>
                                    <Input name="total_weight" value={totalWeight} placeholder="น้ำหนักรวม" disabled />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>ต้นทุนรวม</label>
                                <div>
                                    <Input name="total_cost" value={totalCost} placeholder="ต้นทุนรวม" disabled />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>หมายเหตุ{isUpdate ? <span> *</span> : null}</label>
                                <div>
                                    <Input name="remark" value={remark} placeholder="หมายเหตุ" disabled={!isDraftEditable} onChange={onDataChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', width: '100%' }}>
                    {data.status === 2 ? (
                        <div style={{ color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🟢</span> รายการนี้ได้รับการยืนยันรับเข้าสินค้าและปรับปรุงสต๊อกเรียบร้อยแล้ว (ไม่สามารถแก้ไขได้)
                        </div>
                    ) : (
                        <>
                            {prop.onSaveDraft && (
                                <Button 
                                    type="default" 
                                    onClick={prop.onSaveDraft}
                                    style={{ borderRadius: '8px', fontWeight: 600 }}
                                    data-testid="btn-save-gr-draft"
                                >
                                    💾 บันทึกฉบับร่าง
                                </Button>
                            )}
                            {prop.onConfirmReceive && (
                                <Button 
                                    type="primary" 
                                    onClick={prop.onConfirmReceive}
                                    style={{ background: '#10B981', borderColor: '#10B981', borderRadius: '8px', fontWeight: 700 }}
                                    data-testid="btn-confirm-gr"
                                >
                                    📦 ยืนยันรับเข้าสินค้า
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* <CommonDetail width='40%' mode={'ADD'} title={'รายละเอียดสินค้า'} open={open} onClosed={() => { setOpen(false) }} onSaved={onEditedProduct} 
                element={ <GoodReceiveProduct product={{...product}} onChange={setProduct} /> } 
            /> */}

            <Modal
                closeIcon={false}
                centered
                open={isOpen}
                footer={setFooter()}
                width={780}
                destroyOnClose={true}
            >
                {isOpen && tableSelect ? <CommonSearch table={tableSelect} onSelect={handleSelected} /> : null}
            </Modal>

            <CommonDetail 
                width='40%' 
                mode={'ADD'} 
                title={'เพิ่มสินค้า'} 
                open={openAddProduct} 
                onClosed={onAddProductClosed} 
                onSaved={onSaveProduct} 
                element={ <ProductDetail product={{...addProduct}} onChange={(payload: any) => {setAddProduct(payload)}} /> } 
            />
        </>
    );
}

export default GoodReceiveDetail;